import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import FileUpload from "./FileUpload";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const blankLine = {
  item_id: "",
  qty: 1,
  rate: "",
  brand: "",
  warranty_end: "",
};

const newOperationId = () =>
  globalThis.crypto?.randomUUID?.() ||
  `asset-edit-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const toForm = (purchase) => ({
  vendor_id: purchase?.vendor_id || "none",
  vendor_name: purchase?.vendor_name || "",
  invoice_no: purchase?.invoice_no || "",
  po_number: purchase?.po_number || "",
  bill_date: purchase?.bill_date || purchase?.date || "",
  receiving_location: purchase?.receiving_location || "A_STORE",
  room_id: purchase?.room_id || "none",
  department_id: purchase?.department_id || "none",
  attachment: purchase?.attachment || null,
  remarks: purchase?.remarks || "",
  edit_reason: "",
  operation_id: newOperationId(),
  expected_version: Number(purchase?.version || 0),
  lines: purchase?.lines?.map((line) => ({
    item_id: line.item_id,
    qty: line.qty,
    rate: line.rate ?? "",
    brand: line.brand || "",
    warranty_end: line.warranty_end || "",
  })) || [{ ...blankLine }],
});

export default function AssetPurchaseEditDialog({
  open,
  onOpenChange,
  purchase,
  vendors,
  items,
  rooms,
  departments,
  onSaved,
}) {
  const [form, setForm] = useState(toForm(purchase));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(toForm(purchase));
    }
  }, [open, purchase]);

  const total = useMemo(
    () =>
      form.lines.reduce(
        (sum, line) => sum + Number(line.qty || 0) * Number(line.rate || 0),
        0,
      ),
    [form.lines],
  );

  const updateLine = (index, field, value) => {
    setForm((current) => ({
      ...current,
      lines: current.lines.map((line, lineIndex) =>
        lineIndex === index ? { ...line, [field]: value } : line,
      ),
    }));
  };

  const save = async () => {
    const lines = form.lines.filter(
      (line) => line.item_id && Number(line.qty) > 0,
    );
    if (
      !form.invoice_no.trim() ||
      !form.bill_date ||
      !lines.length ||
      !form.edit_reason.trim()
    ) {
      toast.error(
        "Bill number, bill date, an item, and edit reason are required",
      );
      return;
    }
    if (form.receiving_location === "A_ROOM" && form.room_id === "none") {
      toast.error("Choose the receiving room");
      return;
    }
    if (
      form.receiving_location === "A_DEPARTMENT" &&
      form.department_id === "none"
    ) {
      toast.error("Choose the receiving department");
      return;
    }
    try {
      setSaving(true);
      const { data } = await api.put(`/assets/purchases/${purchase.id}`, {
        vendor_id: form.vendor_id === "none" ? null : form.vendor_id,
        vendor_name: form.vendor_name.trim() || null,
        invoice_no: form.invoice_no.trim(),
        po_number: form.po_number.trim() || null,
        date: form.bill_date,
        bill_date: form.bill_date,
        receiving_location: form.receiving_location,
        room_id: form.receiving_location === "A_ROOM" ? form.room_id : null,
        department_id:
          form.receiving_location === "A_DEPARTMENT"
            ? form.department_id
            : null,
        attachment: form.attachment,
        remarks: form.remarks.trim() || null,
        edit_reason: form.edit_reason.trim(),
        operation_id: form.operation_id,
        expected_version: form.expected_version,
        lines: lines.map((line) => ({
          item_id: line.item_id,
          qty: Number(line.qty),
          rate: line.rate === "" ? null : Number(line.rate),
          brand: line.brand.trim() || null,
          warranty_end: line.warranty_end || null,
        })),
      });
      toast.success("Purchase correction completed and recorded");
      onSaved(data);
      onOpenChange(false);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-6xl max-h-[90vh] overflow-y-auto"
        data-testid="asset-purchase-edit-dialog"
      >
        <DialogHeader>
          <DialogTitle data-testid="asset-purchase-edit-title">
            Edit Asset Purchase
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label>Supplier</Label>
            <Select
              value={form.vendor_id}
              onValueChange={(vendor_id) => setForm({ ...form, vendor_id })}
            >
              <SelectTrigger
                className="h-11 mt-1"
                data-testid="asset-edit-supplier-select"
              >
                <SelectValue placeholder="Select saved supplier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Manual supplier</SelectItem>
                {vendors.map((vendor) => (
                  <SelectItem key={vendor.id} value={vendor.id}>
                    {vendor.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Supplier Name</Label>
            <Input
              value={form.vendor_name}
              onChange={(event) =>
                setForm({ ...form, vendor_name: event.target.value })
              }
              className="h-11 mt-1"
              data-testid="asset-edit-supplier-name"
            />
          </div>
          <div>
            <Label>Bill / Invoice Number</Label>
            <Input
              value={form.invoice_no}
              onChange={(event) =>
                setForm({ ...form, invoice_no: event.target.value })
              }
              className="h-11 mt-1"
              data-testid="asset-edit-bill-number"
            />
          </div>
          <div>
            <Label>Reference / PO Number</Label>
            <Input
              value={form.po_number}
              onChange={(event) =>
                setForm({ ...form, po_number: event.target.value })
              }
              className="h-11 mt-1"
              data-testid="asset-edit-po-number"
            />
          </div>
          <div>
            <Label>Bill Date</Label>
            <Input
              type="date"
              value={form.bill_date}
              onChange={(event) =>
                setForm({ ...form, bill_date: event.target.value })
              }
              className="h-11 mt-1"
              data-testid="asset-edit-bill-date"
            />
          </div>
          <div>
            <Label>Receive Into</Label>
            <Select
              value={form.receiving_location}
              onValueChange={(receiving_location) =>
                setForm({ ...form, receiving_location })
              }
            >
              <SelectTrigger
                className="h-11 mt-1"
                data-testid="asset-edit-receive-into"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="A_STORE">Asset Store</SelectItem>
                <SelectItem value="A_ROOM">Room</SelectItem>
                <SelectItem value="A_DEPARTMENT">Department</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.receiving_location === "A_ROOM" && (
            <div>
              <Label>Receiving Room</Label>
              <Select
                value={form.room_id}
                onValueChange={(room_id) => setForm({ ...form, room_id })}
              >
                <SelectTrigger
                  className="h-11 mt-1"
                  data-testid="asset-edit-room"
                >
                  <SelectValue placeholder="Select room" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select room</SelectItem>
                  {rooms.map((room) => (
                    <SelectItem key={room.id} value={room.id}>
                      Room {room.number}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {form.receiving_location === "A_DEPARTMENT" && (
            <div>
              <Label>Receiving Department</Label>
              <Select
                value={form.department_id}
                onValueChange={(department_id) =>
                  setForm({ ...form, department_id })
                }
              >
                <SelectTrigger
                  className="h-11 mt-1"
                  data-testid="asset-edit-department"
                >
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select department</SelectItem>
                  {departments
                    .filter((department) => department.active)
                    .map((department) => (
                      <SelectItem key={department.id} value={department.id}>
                        {department.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div className="text-sm font-semibold text-slate-700">
            Purchase Lines
          </div>
          {form.lines.map((line, index) => (
            <div
              key={`asset-edit-line-${index}`}
              className="grid grid-cols-1 md:grid-cols-12 gap-2"
            >
              <div className="md:col-span-4">
                <Label className="text-xs">Item</Label>
                <Select
                  value={line.item_id}
                  onValueChange={(value) => updateLine(index, "item_id", value)}
                >
                  <SelectTrigger
                    className="h-11 mt-1"
                    data-testid={`asset-edit-item-${index}`}
                  >
                    <SelectValue placeholder="Item" />
                  </SelectTrigger>
                  <SelectContent>
                    {items.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Quantity</Label>
                <Input
                  type="number"
                  min="1"
                  value={line.qty}
                  onChange={(event) =>
                    updateLine(index, "qty", event.target.value)
                  }
                  className="h-11 mt-1"
                  data-testid={`asset-edit-qty-${index}`}
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Unit Price</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={line.rate}
                  onChange={(event) =>
                    updateLine(index, "rate", event.target.value)
                  }
                  className="h-11 mt-1"
                  data-testid={`asset-edit-rate-${index}`}
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Line Total</Label>
                <div className="h-11 mt-1 flex items-center px-3 border rounded-md bg-slate-50">
                  {(Number(line.qty || 0) * Number(line.rate || 0)).toFixed(2)}
                </div>
              </div>
              <div className="md:col-span-2 flex items-end gap-2">
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  onClick={() =>
                    setForm({
                      ...form,
                      lines: form.lines.filter(
                        (_, lineIndex) => lineIndex !== index,
                      ),
                    })
                  }
                  disabled={form.lines.length === 1}
                  data-testid={`asset-edit-remove-line-${index}`}
                >
                  <Trash2 className="w-4 h-4 text-red-600" />
                </Button>
              </div>
              <div className="md:col-span-4">
                <Label className="text-xs">Brand</Label>
                <Input
                  value={line.brand}
                  onChange={(event) =>
                    updateLine(index, "brand", event.target.value)
                  }
                  className="h-10 mt-1"
                  data-testid={`asset-edit-brand-${index}`}
                />
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs">Warranty End</Label>
                <Input
                  type="date"
                  value={line.warranty_end}
                  onChange={(event) =>
                    updateLine(index, "warranty_end", event.target.value)
                  }
                  className="h-10 mt-1"
                  data-testid={`asset-edit-warranty-end-${index}`}
                />
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              setForm({ ...form, lines: [...form.lines, { ...blankLine }] })
            }
            data-testid="asset-edit-add-line"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Line
          </Button>
          <div
            className="font-semibold text-sm"
            data-testid="asset-edit-total-value"
          >
            Total Bill Value: {total.toFixed(2)}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Remarks</Label>
            <Textarea
              value={form.remarks}
              onChange={(event) =>
                setForm({ ...form, remarks: event.target.value })
              }
              data-testid="asset-edit-remarks"
            />
          </div>
          <div>
            <Label>Invoice Attachment</Label>
            <div className="mt-2">
              <FileUpload
                value={form.attachment}
                onChange={(attachment) => setForm({ ...form, attachment })}
                testid="asset-edit-attachment"
              />
            </div>
          </div>
        </div>
        <div>
          <Label>Edit Reason *</Label>
          <Textarea
            value={form.edit_reason}
            onChange={(event) =>
              setForm({ ...form, edit_reason: event.target.value })
            }
            data-testid="asset-edit-reason"
          />
        </div>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-slate-900 text-white hover:bg-slate-800"
          data-testid="asset-edit-save"
        >
          {saving ? "Saving…" : "Save Audited Correction"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
